import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../lib/api";
import { ShoppingCart, LogOut, AlertTriangle } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";

export default function PosLayout() {
  const { posUser, posLogout } = useAuth();
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [recentRooms, setRecentRooms] = useState([]);
  const [sessionWarning, setSessionWarning] = useState(false);
  const [lastTransaction, setLastTransaction] = useState(null);
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!posUser) navigateRef.current("/pos/login");
  }, [posUser]);

  // Session polling
  useEffect(() => {
    const poll = setInterval(async () => {
      try {
        const res = await api.get("/auth/session-check");
        setSessionWarning(res.data.remaining_seconds <= 120);
      } catch {
        setCart([]);
        setCurrentRoom(null);
        navigateRef.current("/pos/login");
      }
    }, 60000);
    return () => clearInterval(poll);
  }, []);

  const addToCart = useCallback((item, qty = 1) => {
    setCart((prev) => {
      const existing = prev.find((c) => c.id === item.id);
      if (existing) {
        return prev.map((c) => c.id === item.id ? { ...c, quantity: c.quantity + qty } : c);
      }
      return [...prev, { ...item, quantity: qty }];
    });
  }, []);

  const updateQuantity = useCallback((itemId, qty) => {
    setCart((prev) =>
      qty <= 0
        ? prev.filter((c) => c.id !== itemId)
        : prev.map((c) => c.id === itemId ? { ...c, quantity: qty } : c)
    );
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const cartTotal = useMemo(() => cart.reduce((s, i) => s + i.unit_price * i.quantity, 0), [cart]);
  const cartCount = useMemo(() => cart.reduce((s, i) => s + i.quantity, 0), [cart]);

  const handleLogout = useCallback(async () => {
    await posLogout();
    setCart([]);
    setCurrentRoom(null);
    navigateRef.current("/pos/login");
  }, [posLogout]);

  const outletContext = useMemo(() => ({
    cart, addToCart, updateQuantity, clearCart, cartTotal, cartCount,
    currentRoom, setCurrentRoom, recentRooms, setRecentRooms,
    lastTransaction, setLastTransaction
  }), [cart, addToCart, updateQuantity, clearCart, cartTotal, cartCount,
       currentRoom, recentRooms, lastTransaction]);

  if (!posUser) return null;

  return (
    <div className="pos-container" data-testid="pos-layout">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white shrink-0">
        <div className="flex items-center gap-3">
          <div>
            <p className="font-heading text-sm font-bold tracking-tight">MINIBAR</p>
            <p className="text-[9px] text-gray-400 uppercase tracking-[0.15em]">{posUser?.display_name || 'Operator'}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {currentRoom && (
            <span className="text-xs font-bold bg-gray-100 px-2 py-1 border border-gray-200" data-testid="current-room-badge">
              Room {currentRoom.room_number}
            </span>
          )}
          {cartCount > 0 && (
            <button onClick={() => navigate('/pos/cart')} className="relative" data-testid="cart-icon-button">
              <ShoppingCart className="w-5 h-5" />
              <span className="absolute -top-1 -right-1 bg-black text-white text-[9px] w-4 h-4 flex items-center justify-center font-bold">
                {cartCount}
              </span>
            </button>
          )}
          <button onClick={handleLogout} className="text-gray-400 hover:text-red-500" data-testid="pos-logout-button">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Session Warning */}
      <Dialog open={sessionWarning} onOpenChange={setSessionWarning}>
        <DialogContent className="rounded-none border-gray-200">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" /> Session Expiring
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">Your session will expire in less than 2 minutes. Please save your work.</p>
          <DialogFooter>
            <Button onClick={() => setSessionWarning(false)} className="rounded-none bg-black text-white">
              Continue Working
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <Outlet context={outletContext} />
      </div>
    </div>
  );
}
